import { Box, Button, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import styles from "./editUser.module.css";
import { useSnackbar } from "notistack";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../consts";
import { useCRUD } from "../../../api/crud";
import { USERS_URL } from "../../../api/users";
import { User } from "../../../types/users";
import { PERMISSION_GROUPS_URL } from "../../../api/permissions";
import { PermissionGroup } from "../../../types/permissions";
import { useAuthContext } from "../../../contexts/auth/auth.hooks";
import CustomAutocomplete from "../../../components/ui/customAutocomplete/customAutocomplete.component";

interface EditUserProps {
  user?: User;
  onSuccess?: () => void;
}

export const EditUser = ({ user, onSuccess }: EditUserProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { update, error } = useCRUD(USERS_URL);
  const { query: queryPermissionGroups, loading: groupsLoading } = useCRUD(PERMISSION_GROUPS_URL);
  const { currentUser } = useAuthContext();

  const defaultFormData = { firstName: "", lastName: "", groups: [] as PermissionGroup[] };
  const [formData, setFormData] = useState(defaultFormData);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);

  const isCurrentUser = user?.id === currentUser?.id;

  useEffect(() => {
    const loadPermissionGroups = async () => {
      const data = await queryPermissionGroups({ page: 0, pageSize: 1000, sortModel: [], filterModel: { items: [] } });
      if (data?.results) {
        setPermissionGroups(data.results);
      }
    };
    loadPermissionGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || "",
        lastName: user.last_name || "",
        groups: user.groups || [],
      });
    }
  }, [user]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditUserSubmit = async () => {
    if (!user?.id) return;

    const result = await update({
      id: user.id,
      first_name: formData.firstName,
      last_name: formData.lastName,
      groups_ids: formData.groups.map((group) => group.id),
    });

    if (result?.success) {
      window.dispatchEvent(new Event("tenantUpdated"));
      onSuccess?.();
      enqueueSnackbar(t("User updated successfully"), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t("User update failed"), ERROR_SNACKBAR);
    }
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="firstName"
          label={t("First name")}
          size="small"
          fullWidth
          value={formData.firstName}
          onChange={handleFormChange}
          required
        />
        {error && error?.first_name && (
          <div className={styles.errorMessage}>
            {error.first_name[0]?.message}
          </div>
        )}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="lastName"
          label={t("Last name")}
          size="small"
          fullWidth
          value={formData.lastName}
          onChange={handleFormChange}
          required
        />
        {error && error?.last_name && (
          <div className={styles.errorMessage}>
            {error.last_name[0]?.message}
          </div>
        )}
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <TextField
          name="email"
          label={t("Email")}
          size="small"
          fullWidth
          value={user?.email || ""}
          disabled
          required
        />
      </Box>
      <Box sx={{ flex: "1 1 45%", maxWidth: "calc(50% - 8px)" }}>
        <CustomAutocomplete
          multiple
          options={permissionGroups}
          getOptionLabel={(option) => option.name}
          value={formData.groups}
          onChange={(_, newValue) => {
            setFormData((prev) => ({ ...prev, groups: newValue }));
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={groupsLoading}
          disabled={isCurrentUser}
          size="small"
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Permission Groups")}
              placeholder={t("Select permission groups")}
              helperText={isCurrentUser ? t("You cannot modify your own permission groups") : undefined}
            />
          )}
        />
        {error && error?.groups_ids && (
          <div className={styles.errorMessage}>
            {error.groups_ids[0]?.message}
          </div>
        )}
      </Box>
      <Box sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleEditUserSubmit}
        >
          {t("Update user")}
        </Button>
      </Box>
    </Box>
  );
};
